import { useEffect, useState } from "react";

import { Plus } from "lucide-react";
import { toast } from "sonner";

import DocumentUploadInput from "../../../components/shared/DocumentUploadInput";
import LazySearchSelect from "../../../components/shared/LazySearchSelect.jsx";
import WordCount from "../../../components/shared/WordCount.jsx";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { useApi } from "../../../context/ApiContext";
import { useAuth } from "../../../context/AuthContext";
import { cn, getCharCount } from "../../../lib/utils.js";
import { formatDateForInput } from "../../../utils/dateUtils";
import InvoiceDialog from "../../Invoices/component/InvoiceDialog";
import VendorDialog from "../../Vendors/component/VendorDialog";

const MaterialForm = ({ isOpen, setIsOpen, loadMaterials, editingMaterialId, setEditingMaterialId, selectedProject }) => {
  const { user } = useAuth();
  const api = useApi();
  const [photoPreview, setPhotoPreview] = useState([]);
  const [newMaterial, setNewMaterial] = useState({
    projectId: "",
    name: "",
    quantity: "",
    unit: "",
    costPerUnit: "",
    vendorId: "",
    referenceNumber: "",
    invoiceId: "",
    deliveredDate: "",
    notes: ""
  });

  const [isCreateVendorOpen, setIsCreateVendorOpen] = useState(false);
  const [isCreateInvoiceOpen, setIsCreateInvoiceOpen] = useState(false);

  const [selectedProjectDates, setSelectedProjectDates] = useState({
    startDate: "",
    endDate: ""
  });

  const isNotesOverLimit = getCharCount(newMaterial.notes) > 500;

  useEffect(() => {
    if (editingMaterialId && isOpen) {
      fetchMaterialDetails(editingMaterialId);
    } else if (!editingMaterialId && isOpen) {
      // Reset for new creation
      setNewMaterial({
        projectId: selectedProject || "",
        name: "",
        quantity: "",
        unit: "",
        costPerUnit: "",
        vendorId: "",
        referenceNumber: "",
        invoiceId: "",
        deliveredDate: "",
        notes: ""
      });
      setPhotoPreview([]);
    }
  }, [editingMaterialId, isOpen, selectedProject, user?.id]);

  const fetchMaterialDetails = async (materialId) => {
    try {
      const response = await api.get(`/materials/id/${materialId}`);
      const materialData = response.data;

      const formattedDate = materialData.deliveredDate ? new Date(materialData.deliveredDate).toISOString().split("T")[0] : "";

      setNewMaterial({
        projectId: materialData.project?.id || materialData.projectId || "",
        name: materialData.name || "",
        quantity: materialData.quantity != null ? String(materialData.quantity) : "",
        unit: materialData.unit || "",
        costPerUnit: materialData.costPerUnit != null ? String(materialData.costPerUnit) : "",
        vendorId: materialData.supplier?.id || materialData.supplierId || materialData.vendorId || materialData.supplier || "",
        referenceNumber: materialData.challanSlipNumber || "",
        invoiceId: materialData.invoice?.id || materialData.invoiceId || "",
        deliveredDate: formattedDate,
        notes: materialData.notes || ""
      });

      if (materialData.photos && materialData.photos.length > 0) {
        setPhotoPreview(materialData.photos);
      } else {
        setPhotoPreview([]);
      }
    } catch (error) {
      toast.error("Failed to fetch material details");
      setIsOpen(false);
    }
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const readers = [];
      files.forEach((file) => {
        const reader = new FileReader();
        readers.push(
          new Promise((resolve) => {
            reader.onloadend = () => {
              resolve(reader.result);
            };
            reader.readAsDataURL(file);
          })
        );
      });

      Promise.all(readers).then((results) => {
        setPhotoPreview((prev) => [...prev, ...results]);
      });
    }
  };

  const removePhoto = (index) => {
    setPhotoPreview((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isNotesOverLimit) {
      toast.error("Notes exceed the 500-character limit.");
      return;
    }
    try {
      if (!newMaterial.projectId && !selectedProject) {
        toast.error("Please select a project");
        return;
      }

      const requiredFields = [
        { key: "name", label: "Material Name" },
        { key: "quantity", label: "Quantity" },
        { key: "unit", label: "Unit" },
        { key: "costPerUnit", label: "Cost Per Unit" },
        { key: "vendorId", label: "Vendor" },
        { key: "invoiceId", label: "Invoice" },
        { key: "deliveredDate", label: "Delivery Date" }
      ];

      const missingFields = requiredFields.filter((f) => !newMaterial[f.key]);
      if (missingFields.length > 0) {
        toast.error(`Please fill in all required fields: ${missingFields.map((f) => f.label).join(", ")}`);
        return;
      }

      const payload = {
        ...newMaterial,
        projectId: newMaterial.projectId || selectedProject,
        quantity: parseFloat(newMaterial.quantity),
        costPerUnit: newMaterial.costPerUnit ? parseFloat(newMaterial.costPerUnit) : null,
        deliveredDate: newMaterial.deliveredDate ? new Date(newMaterial.deliveredDate).toISOString() : null,
        challanSlipNumber: newMaterial.referenceNumber,
        supplier: newMaterial.vendorId,
        photos: photoPreview
      };

      if (editingMaterialId) {
        await api.put(`/materials/${editingMaterialId}`, payload);
        toast.success("Material updated successfully!");
      } else {
        await api.post(`/materials`, payload);
        toast.success("Material added successfully!");
      }
      loadMaterials(0);
      setIsOpen(false);
    } catch (error) {
      toast.error(editingMaterialId ? "Failed to update material" : "Failed to add material");
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open) {
            setEditingMaterialId(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden flex flex-col">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle>{editingMaterialId ? "Edit Material" : "Add New Material"}</DialogTitle>
              <DialogDescription>
                {editingMaterialId ? "Update material information" : "Record material delivery and inventory with photos"}
              </DialogDescription>
            </DialogHeader>
          </div>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <Label htmlFor="projectId">Project *</Label>
                <LazySearchSelect
                  value={newMaterial.projectId || selectedProject}
                  onChange={(value, project) => {
                    setNewMaterial({ ...newMaterial, projectId: value });
                    if (project) {
                      setSelectedProjectDates({
                        startDate: project.startDate || "",
                        endDate: project.endDate || ""
                      });
                    }
                  }}
                  placeholder="Select project"
                  searchPlaceholder="Search projects..."
                  apiUrl="/projects"
                  pageSize={20}
                  popoverWidth="w-[var(--radix-popover-trigger-width)] md:w-[400px]"
                />
              </div>
              <div>
                <Label htmlFor="name">
                  Material Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={newMaterial.name}
                  onChange={(e) => setNewMaterial({ ...newMaterial, name: e.target.value })}
                  placeholder="e.g., Concrete, Steel bars, Bricks"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="quantity">
                    Quantity <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="quantity"
                    type="number"
                    step="0.01"
                    value={newMaterial.quantity}
                    onChange={(e) =>
                      setNewMaterial({
                        ...newMaterial,
                        quantity: e.target.value
                      })
                    }
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="unit">
                    Unit <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="unit"
                    value={newMaterial.unit}
                    onChange={(e) => setNewMaterial({ ...newMaterial, unit: e.target.value })}
                    placeholder="e.g., tons, pieces, m³"
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="costPerUnit">
                  Cost Per Unit (₹) <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="costPerUnit"
                  type="number"
                  step="0.01"
                  value={newMaterial.costPerUnit}
                  required
                  onChange={(e) =>
                    setNewMaterial({
                      ...newMaterial,
                      costPerUnit: e.target.value
                    })
                  }
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <Label htmlFor="vendorId">
                    Vendor <span className="text-red-500">*</span>
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px] text-yale-blue-600 px-2"
                    onClick={() => setIsCreateVendorOpen(true)}
                  >
                    <Plus className="w-3 h-3 mr-1" /> New Vendor
                  </Button>
                </div>
                <LazySearchSelect
                  value={newMaterial.vendorId}
                  onChange={(value) => setNewMaterial({ ...newMaterial, vendorId: value })}
                  placeholder="Select Vendor"
                  searchPlaceholder="Search vendors..."
                  apiUrl="/vendors"
                  labelKey="vendorName"
                  pageSize={20}
                  isClearable={true}
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <Label htmlFor="invoiceId">
                    Linked Invoice <span className="text-red-500">*</span>
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px] text-yale-blue-600 px-2"
                    onClick={() => setIsCreateInvoiceOpen(true)}
                  >
                    <Plus className="w-3 h-3 mr-1" /> New Invoice
                  </Button>
                </div>
                <LazySearchSelect
                  value={newMaterial.invoiceId}
                  onChange={(value) => setNewMaterial({ ...newMaterial, invoiceId: value })}
                  placeholder="Select invoice"
                  searchPlaceholder="Search invoices..."
                  apiUrl={newMaterial.vendorId ? `/invoices/vendor/${newMaterial.vendorId}` : "/invoices"}
                  valueFetchUrl={newMaterial.invoiceId ? `/invoices/${newMaterial.invoiceId}` : null}
                  labelKey="invoiceNumber"
                  pageSize={20}
                  disabled={!newMaterial.vendorId && !newMaterial.invoiceId}
                  isClearable={true}
                />
              </div>
              <div>
                <Label htmlFor="referenceNumber">Payment Reference Number</Label>
                <Input
                  id="referenceNumber"
                  value={newMaterial.referenceNumber}
                  onChange={(e) =>
                    setNewMaterial({
                      ...newMaterial,
                      referenceNumber: e.target.value
                    })
                  }
                  placeholder="Enter payment reference number"
                />
              </div>
              <div>
                <Label htmlFor="deliveredDate">
                  Delivery Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="deliveredDate"
                  type="date"
                  value={newMaterial.deliveredDate}
                  required
                  max={formatDateForInput(selectedProjectDates.endDate)}
                  onChange={(e) =>
                    setNewMaterial({
                      ...newMaterial,
                      deliveredDate: e.target.value
                    })
                  }
                  disabled={!newMaterial.projectId}
                />
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={newMaterial.notes}
                  onChange={(e) => setNewMaterial({ ...newMaterial, notes: e.target.value })}
                  className={cn(isNotesOverLimit && "border-red-500")}
                  rows={3}
                  placeholder="Additional notes about this material"
                />
                <WordCount text={newMaterial.notes} />
              </div>

              <DocumentUploadInput
                id="material-photo-upload"
                value={photoPreview}
                onChange={(val) => setPhotoPreview(val || [])}
                label="Material Photos & Documents"
                multiple={true}
                helperText="Upload photos or PDF documents related to the materials"
              />
            </div>
            <div className="p-6 pt-2 border-t bg-white dark:bg-yale-blue-900 sticky bottom-0 z-10">
              <Button type="submit" variant="primary" className="w-full py-6 rounded-lg shadow-lg" disabled={isNotesOverLimit}>
                {editingMaterialId ? "Update Material" : "Add Material"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* Create Vendor Dialog */}
      <VendorDialog
        isOpen={isCreateVendorOpen}
        onOpenChange={setIsCreateVendorOpen}
        onSuccess={(vendor) => {
          setNewMaterial((prev) => ({ ...prev, vendorId: vendor.id }));
        }}
        selectedProject={newMaterial.projectId || selectedProject}
      />

      {/* Create Invoice Dialog */}
      <InvoiceDialog
        isOpen={isCreateInvoiceOpen}
        onOpenChange={setIsCreateInvoiceOpen}
        onSuccess={(invoice) => {
          setNewMaterial((prev) => ({ ...prev, invoiceId: invoice.id }));
        }}
        selectedProject={newMaterial.projectId || selectedProject}
        selectedVendor={newMaterial.vendorId}
        initialAmount={parseFloat(newMaterial.quantity || 0) * parseFloat(newMaterial.costPerUnit || 0)}
      />
    </>
  );
};

export default MaterialForm;
